import { useSessionStorage, useStorage } from "@vueuse/core"

import { createAuth0 } from "@auth0/auth0-vue"
import type { GenericError as Auth0GenericError } from "@auth0/auth0-spa-js"
import type { RouteLocationNormalized } from "vue-router"
import type { Auth0VueClient } from "@auth0/auth0-vue"

export enum AuthRoutePaths {
  login = "/auth/login",
  logout = "/auth/logout",
  callback = "/auth/callback",
}

function safeRequestedUrl(value: string | undefined | null): string {
  const fallback_route = "/"
  let requestedUrl = value || fallback_route
  if (Object.values(AuthRoutePaths).includes(requestedUrl as AuthRoutePaths)) {
    requestedUrl = fallback_route
  }
  return requestedUrl
}

type LoginHints = {
  org_id?: string
  connection?: string
}

// just store some info to ease re-connect
const auth0Metadata = useStorage<LoginHints>("auth0-metadata-zefire", {
  org_id: undefined,
  connection: undefined,
})

// Message of the login denied on purpose by the Auth0 post-login Action "Inherit org roles on SSO
// connection switch": keep it in sync with its `RELOGIN_MESSAGE`.
export const ROLES_INHERITED_DENIAL_MESSAGE =
  "Vos droits d'accès ont été transférés vers votre nouveau compte. Merci de vous reconnecter."
const ROLES_INHERITED_DENIAL_HINTS_PATTERN = /^ \[organization=(\S+) connection=(\S+)\]$/

/**
 * Parse the description of the login denied after inheriting roles: ROLES_INHERITED_DENIAL_MESSAGE,
 * optionally followed by the login hints of the denied login, e.g.
 * "<message> [organization=org_XcQh2O3pV7aSWTH0 connection=mexens-ad]".
 *
 * Return undefined for any other description.
 */
function parseRolesInheritedDenial(description: string): LoginHints | undefined {
  if (!description.startsWith(ROLES_INHERITED_DENIAL_MESSAGE)) {
    return undefined
  }

  const suffix = description.slice(ROLES_INHERITED_DENIAL_MESSAGE.length)
  if (suffix === "") {
    return {}
  }

  const match = ROLES_INHERITED_DENIAL_HINTS_PATTERN.exec(suffix)
  return match ? { org_id: match[1], connection: match[2] } : undefined
}

// Timestamp of the last automatic re-login after a denied login, so that it is retried only once.
const deniedLoginRetriedAt = useSessionStorage<number | null>(
  "auth0-denied-login-retried-at-zefire",
  null,
)
const DENIED_LOGIN_RETRY_COOLDOWN_MS = 5 * 60 * 1000

/**
 * Tell whether a failed login callback can be recovered by starting a new login.
 *
 * - `access_denied` with ROLES_INHERITED_DENIAL_MESSAGE: the first login through a new SSO
 *   connection inherits the organization roles of the legacy account, but the token being issued
 *   does not carry the inherited permissions yet. Retry once, in the same organization and
 *   connection: the Auth0 session makes it silent.
 * - any other error but `access_denied` while a connection hint is stored: the hint may be stale,
 *   e.g. the organization switched to a new SSO connection. Forget it and retry.
 */
export function shouldRetryLogin(error: Auth0GenericError): boolean {
  const deniedLoginHints =
    error.error === "access_denied" ? parseRolesInheritedDenial(error.error_description) : undefined
  if (deniedLoginHints) {
    const lastRetry = deniedLoginRetriedAt.value
    const hasRecentlyRetried =
      R.isNumber(lastRetry) && Date.now() - lastRetry < DENIED_LOGIN_RETRY_COOLDOWN_MS
    if (hasRecentlyRetried) {
      return false
    }

    deniedLoginRetriedAt.value = Date.now()
    auth0Metadata.value = { ...auth0Metadata.value, ...deniedLoginHints }
    return true
  }

  const hasStaleConnectionHint =
    error.error !== "access_denied" && R.isTruthy(auth0Metadata.value.connection)
  if (hasStaleConnectionHint) {
    auth0Metadata.value.connection = undefined
    return true
  }

  return false
}

export async function login(auth0: Auth0VueClient, route: RouteLocationNormalized): Promise<void> {
  const fn = auth0.loginWithRedirect
  if (route.query && route.query.invitation && route.query.organization) {
    // Handling invitation
    await fn({
      authorizationParams: {
        invitation: route.query.invitation as string,
        organization: route.query.organization as string,
      },
    })
  } else {
    const requestedUrl = safeRequestedUrl(route.fullPath)
    await fn({
      appState: { requestedUrl },
      authorizationParams: {
        organization: auth0Metadata.value?.org_id,
        connection: auth0Metadata.value?.connection,
      },
      async openUrl(url) {
        await navigateTo(url, { external: true })
      },
    })
  }
}

export async function logout(auth0: Auth0VueClient, opts?: { switchToOrgId: string }) {
  useNuxtApp().$posthog?.reset()

  if (opts?.switchToOrgId) {
    auth0Metadata.value.org_id = opts.switchToOrgId
  } else {
    auth0Metadata.value = { org_id: undefined, connection: undefined }
  }
  await auth0.logout({ logoutParams: { returnTo: window.location.origin } })
}

export async function handleCallback(auth0: Auth0VueClient): Promise<string> {
  const { appState } = await auth0.handleRedirectCallback()

  if (!auth0.isAuthenticated.value) {
    throw new Error("Failed to auth")
  }

  const user = auth0.user.value!
  const claims = auth0.idTokenClaims.value!

  deniedLoginRetriedAt.value = null
  auth0Metadata.value = {
    org_id: user.org_id || undefined,
    connection: claims["https://renewex.co"].connection || undefined,
  }

  // appState is undefined in case of invitation
  return safeRequestedUrl(appState?.requestedUrl as any)
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()

  const auth0 = createAuth0(
    {
      domain: config.public.auth0.domain,
      clientId: config.public.auth0.clientId,
      cacheLocation: "localstorage",
      useRefreshTokens: true,
      authorizationParams: {
        display: "page",
        max_age: config.public.auth0.max_age,
        audience: config.public.auth0.audience,
        redirect_uri: `${window.location.origin}${AuthRoutePaths.callback}`,
      },
    },
    {
      skipRedirectCallback: true,
      errorPath: "/error",
    },
  )

  if (import.meta.client) {
    nuxtApp.vueApp.use(auth0)
  }
})
